<script setup lang="ts">
import { computed } from 'vue'

import {
  DAYS_OF_WEEK,
  HOUR_RANGES,
  availableExportFrequencies,
  useScheduleTimeInfo,
} from '../../../composables/useScheduleTimeInfo'
import type {
  ExportDayOfWeek,
  ExportFrequency,
  ExportScheduleDraft,
} from '../../../models/export.types'

import { TIMEZONE_OPTIONS } from '@/shared/config'
import { useI18n } from '@/shared/i18n'
import { Input } from '@/shared/ui/input'
import { Select } from '@/shared/ui/select'
import type { SelectOption } from '@/shared/ui/select'

const props = defineProps<{ allowedFrequencies: readonly string[] }>()
const model = defineModel<ExportScheduleDraft>({ required: true })
const { t } = useI18n()

const frequency = fieldModel('frequency')
const hour = fieldModel('hour')
const timezone = fieldModel('timezone')
const dayOfWeek = fieldModel('dayOfWeek')
const dayOfMonth = fieldModel('dayOfMonth')

const frequencyOptions = computed<SelectOption<ExportFrequency>[]>(() =>
  availableExportFrequencies(props.allowedFrequencies).map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  })),
)
const hourOptions = computed<SelectOption<number>[]>(() =>
  HOUR_RANGES.map((option) => ({ value: option.value, label: t(option.labelKey) })),
)
const timezoneOptions = computed<SelectOption<string>[]>(() =>
  TIMEZONE_OPTIONS.map((option) => ({
    value: option.timezone,
    label: `${option.timezone} (${t('dataExport.schedule.utcOffset', { offset: option.utc_offset })})`,
  })),
)
const dayOptions = computed<SelectOption<ExportDayOfWeek>[]>(() =>
  DAYS_OF_WEEK.map((option) => ({ value: option.value, label: t(option.labelKey) })),
)
const timeInfo = computed(() => useScheduleTimeInfo(model.value))

function fieldModel<K extends keyof ExportScheduleDraft>(key: K) {
  return computed<ExportScheduleDraft[K]>({
    get: () => model.value[key],
    set: (value) => {
      model.value = { ...model.value, [key]: value }
    },
  })
}

defineExpose({ timeInfo })
</script>

<template>
  <section class="export-schedule grid gap-sm" :aria-label="t('dataExport.schedule.title')">
    <Select
      v-model="frequency"
      :options="frequencyOptions"
      :label="t('dataExport.schedule.frequencyLabel')"
      :empty-label="t('dataExport.common.noResults')"
    />
    <p class="export-schedule__hint text-subtle text-xs">
      {{ t('dataExport.schedule.frequencyHint') }}
    </p>

    <div
      v-if="frequency !== 'once'"
      class="export-schedule__fields grid grid-cols-[repeat(auto-fit,_minmax(12rem,_1fr))] gap-md mt-sm"
    >
      <Select
        v-model="hour"
        :options="hourOptions"
        :label="t('dataExport.schedule.hourLabel')"
        :empty-label="t('dataExport.common.noResults')"
      />
      <Select
        v-model="timezone"
        :options="timezoneOptions"
        :label="t('dataExport.schedule.timezoneLabel')"
        :empty-label="t('dataExport.common.noResults')"
        searchable
      />
      <Select
        v-if="frequency === 'weekly'"
        v-model="dayOfWeek"
        :options="dayOptions"
        :label="t('dataExport.schedule.dayOfWeekLabel')"
        :empty-label="t('dataExport.common.noResults')"
      />
      <Input
        v-if="frequency === 'monthly'"
        v-model="dayOfMonth"
        type="number"
        :min="1"
        :max="31"
        :label="t('dataExport.schedule.dayOfMonthLabel')"
      />
    </div>
  </section>
</template>
